import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import {
  ArrowLeft,
  Upload,
  ClipboardPaste,
  FileJson,
  Image as ImageIcon,
  X,
  AlertCircle,
  Check,
} from 'lucide-react';
import { api, ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { cn } from '../lib/cn';

interface Props {
  onCreated: (projectId: string) => void;
  onCancel: () => void;
}

// Mirrors packages/site-kits/kits/ on the engine. The engine doesn't expose
// a /api/kits endpoint yet — bump this list when a new kit ships.
const KIT_OPTIONS = [
  { id: 'roofing-premium-v1', label: 'Roofing Premium', description: 'Roofing contractors, multi-service + multi-location' },
  { id: 'gates-premium-v1', label: 'Gates Premium', description: 'Fence & gate installers, multi-service + multi-location' },
];

const PLACEHOLDER_BRAND_JSON = `{
  "business": {
    "name": "Your Business Name",
    "tagline": "Catchy tagline",
    "description": "What your business does and who it serves.",
    "yearsExperience": 10,
    "openingHours": {
      "monday": "09:00-17:00",
      "tuesday": "09:00-17:00",
      "wednesday": "09:00-17:00",
      "thursday": "09:00-17:00",
      "friday": "09:00-17:00"
    },
    "ctaText": "Get a Free Estimate"
  },
  "contact": {
    "phone": "+19999999999",
    "email": "hello@example.com",
    "address": { "line1": "...", "city": "...", "state": "NY", "postalCode": "10000", "country": "US" }
  },
  "branding": { "primaryColor": "#0066cc", "logoUrl": "" },
  "coverage": { "boroughs": ["Manhattan"], "neighborhoods": [] },
  "services": ["Service A", "Service B"]
}`;

const LOGO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif', 'avif'] as const;
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

function getLogoFormat(file: File): string {
  const nameExt = file.name.split('.').pop()?.toLowerCase();
  if (nameExt && (LOGO_EXTENSIONS as readonly string[]).includes(nameExt)) return nameExt;
  const m = file.type.toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpeg';
  if (m.includes('svg')) return 'svg';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  if (m.includes('avif')) return 'avif';
  return 'png';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function fileToText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function CreateSiteScreen({ onCreated, onCancel }: Props) {
  const toast = useToast();
  const [kitId, setKitId] = useState(KIT_OPTIONS[0]!.id);
  const [brandJson, setBrandJson] = useState('');
  const [brandFileName, setBrandFileName] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const brandFileInput = useRef<HTMLInputElement>(null);
  const logoFileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const validJsonPreview = useMemo<
    | { ok: true; business: string; services: number; locations: number; pages: number }
    | { ok: false; reason?: string }
    | null
  >(() => {
    if (!brandJson.trim()) return null;
    try {
      const v = JSON.parse(brandJson) as Record<string, unknown>;
      if (typeof v !== 'object' || v === null) return { ok: false };
      const business = (v.business as { name?: string })?.name ?? '';
      const services = Array.isArray((v as { services?: unknown[] }).services)
        ? (v as { services?: unknown[] }).services!.length
        : 0;
      const cov = (v as { coverage?: { boroughs?: unknown[]; neighborhoods?: unknown[] } }).coverage;
      const locations =
        (Array.isArray(cov?.boroughs) ? cov!.boroughs!.length : 0) +
        (Array.isArray(cov?.neighborhoods) ? cov!.neighborhoods!.length : 0);
      const pages = services * locations + services + locations + 5;
      return { ok: true, business, services, locations, pages };
    } catch {
      return { ok: false };
    }
  }, [brandJson]);

  function tryParse(): unknown | null {
    setParseError(null);
    try {
      const v = JSON.parse(brandJson);
      if (typeof v !== 'object' || v === null) {
        setParseError('Brand JSON must be an object');
        return null;
      }
      return v;
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}`);
      return null;
    }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    const brand = tryParse();
    if (!brand) return;

    let logoPayload: { fileBase64: string; format: string } | undefined;
    if (logoFile) {
      try {
        const fileBase64 = await fileToBase64(logoFile);
        logoPayload = { fileBase64, format: getLogoFormat(logoFile) };
      } catch (err) {
        setSubmitError(`Failed to read logo file: ${(err as Error).message}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const r = await api.createProject({
        brand,
        kitId,
        ...(logoPayload ? { logo: logoPayload } : {}),
      });
      if (r.warnings && r.warnings.length > 0) {
        toast.show(`Created with ${r.warnings.length} warning(s) — see project detail`, 'info');
      } else {
        toast.success('Site is being generated');
      }
      onCreated(r.projectId);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function loadTemplate(): void {
    setBrandJson(PLACEHOLDER_BRAND_JSON);
    setBrandFileName(null);
    setParseError(null);
  }

  function handlePaste(): void {
    navigator.clipboard
      .readText()
      .then((t) => {
        setBrandJson(t);
        setBrandFileName(null);
        setParseError(null);
      })
      .catch(() => {
        toast.error('Could not read clipboard — paste manually');
      });
  }

  async function onBrandFileChange(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await fileToText(file);
      setBrandJson(text);
      setBrandFileName(file.name);
      setParseError(null);
    } catch (err) {
      setParseError(`Failed to read file: ${(err as Error).message}`);
    }
  }

  function onLogoFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    setLogoError(null);
    if (!file) return;
    if (
      !file.type.startsWith('image/') &&
      !(LOGO_EXTENSIONS as readonly string[]).includes(file.name.split('.').pop()?.toLowerCase() ?? '')
    ) {
      setLogoError('File must be an image (png, jpg, svg, webp, gif, avif).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`Logo is ${formatBytes(file.size)} — max ${formatBytes(MAX_LOGO_BYTES)}.`);
      return;
    }
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  }

  function clearLogo(): void {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setLogoError(null);
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-5">
      <div>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-[12.5px] text-zinc-500 hover:text-zinc-900 -ml-1 px-1 py-0.5 rounded transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sites
        </button>
      </div>

      <header>
        <h1 className="text-2xl sm:text-[26px] font-semibold tracking-tight2 text-zinc-900">
          Create a new site
        </h1>
        <p className="text-[13.5px] text-zinc-500 mt-1 max-w-xl">
          Provide the brand JSON and optionally a logo file. We'll research,
          write content, generate images and build a live preview — usually
          about 20 minutes for the first build.
        </p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Site kit</CardTitle>
            <CardDescription>The template that defines page structure and styling.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {KIT_OPTIONS.map((k) => {
                const active = kitId === k.id;
                return (
                  <label
                    key={k.id}
                    className={cn(
                      'relative flex items-start gap-3 rounded-lg border px-3.5 py-3 cursor-pointer transition-colors',
                      active
                        ? 'border-zinc-900 bg-zinc-50/50'
                        : 'border-zinc-200 hover:bg-zinc-50',
                      submitting && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <input
                      type="radio"
                      name="kitId"
                      value={k.id}
                      checked={active}
                      onChange={() => setKitId(k.id)}
                      disabled={submitting}
                      className="sr-only"
                    />
                    <div
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                        active ? 'border-zinc-900' : 'border-zinc-300',
                      )}
                    >
                      {active && <span className="h-1.5 w-1.5 rounded-full bg-zinc-900" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-medium text-zinc-900">
                        {k.label}
                      </div>
                      <div className="text-[12px] text-zinc-500 leading-relaxed mt-0.5 text-pretty">
                        {k.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-zinc-400" />
              Logo
              <span className="text-[12px] font-normal text-zinc-400 ml-1">optional</span>
            </CardTitle>
            <CardDescription>
              Replaces <code className="font-mono text-[11.5px] bg-zinc-100 px-1 rounded">branding.logoUrl</code> in the brand JSON.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logoPreviewUrl ? (
              <div className="flex items-center gap-3 p-3 border border-zinc-200 rounded-lg bg-zinc-50/50">
                <img
                  src={logoPreviewUrl}
                  alt="Logo preview"
                  className="h-16 w-16 object-contain bg-white border border-zinc-200 rounded-md"
                />
                <div className="text-[12.5px] min-w-0 flex-1">
                  <div className="font-medium text-zinc-900 truncate">{logoFile!.name}</div>
                  <div className="text-zinc-500">
                    {formatBytes(logoFile!.size)} · {getLogoFormat(logoFile!).toUpperCase()}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={clearLogo}
                  disabled={submitting}
                  aria-label="Remove logo"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoFileInput.current?.click()}
                className={cn(
                  'w-full flex flex-col items-center justify-center gap-2 px-4 py-6 border border-dashed border-zinc-300 rounded-lg text-[13px] text-zinc-500',
                  'hover:border-zinc-400 hover:bg-zinc-50 hover:text-zinc-700 transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900',
                )}
                disabled={submitting}
              >
                <Upload className="h-4 w-4" />
                Click to upload (PNG, JPG, SVG, WebP — up to 5 MB)
              </button>
            )}
            <input
              ref={logoFileInput}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/avif"
              className="hidden"
              onChange={onLogoFileChange}
              disabled={submitting}
            />
            {logoError && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-[12.5px] rounded-lg">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                {logoError}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="h-4 w-4 text-zinc-400" />
              Brand JSON
              {brandFileName && (
                <span className="text-[12px] font-normal text-zinc-400 ml-1">
                  loaded from {brandFileName}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Paste, upload or build with the{' '}
              <code className="font-mono text-[11.5px] bg-zinc-100 px-1 rounded">build_brandjson_skill.md</code>{' '}
              guide.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => brandFileInput.current?.click()}
                leftIcon={<Upload className="h-3 w-3" />}
                disabled={submitting}
              >
                Upload .json
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePaste}
                leftIcon={<ClipboardPaste className="h-3 w-3" />}
                disabled={submitting}
              >
                Paste
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={loadTemplate}
                disabled={submitting}
              >
                Load template
              </Button>
            </div>

            <input
              ref={brandFileInput}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={onBrandFileChange}
              disabled={submitting}
            />

            <Textarea
              value={brandJson}
              onChange={(e) => {
                setBrandJson(e.target.value);
                setBrandFileName(null);
                setParseError(null);
              }}
              placeholder={PLACEHOLDER_BRAND_JSON}
              spellCheck={false}
              wrap="soft"
              required
              disabled={submitting}
              className="h-80 font-mono text-[12px] leading-relaxed"
            />

            {validJsonPreview && validJsonPreview.ok && validJsonPreview.business && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 px-3 py-3 rounded-lg bg-zinc-50 border border-zinc-200 text-[12px]">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide text-zinc-400">Brand</div>
                  <div className="font-medium text-zinc-900 truncate">{validJsonPreview.business}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide text-zinc-400">Services</div>
                  <div className="font-medium text-zinc-900">{validJsonPreview.services}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide text-zinc-400">Locations</div>
                  <div className="font-medium text-zinc-900">{validJsonPreview.locations}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide text-zinc-400">Est. pages</div>
                  <div className="font-medium text-zinc-900">~{validJsonPreview.pages}</div>
                </div>
              </div>
            )}

            {parseError && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-[12.5px] rounded-lg">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{parseError}</span>
              </div>
            )}

            {validJsonPreview?.ok && !parseError && (
              <div className="mt-3 flex items-center gap-1.5 text-[12px] text-emerald-700">
                <Check className="h-3 w-3" />
                Valid JSON
              </div>
            )}
          </CardContent>
        </Card>

        {submitError && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-800 text-[13px] rounded-lg">
            <div className="font-medium mb-1">The engine rejected the request</div>
            <div className="whitespace-pre-wrap break-words font-mono text-[12px] text-red-700/90">
              {submitError}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 sticky bottom-3 z-10 bg-zinc-50/85 backdrop-blur-md rounded-xl p-2 -m-2 sm:static sm:bg-transparent sm:backdrop-blur-none sm:p-0 sm:m-0">
          <Button
            type="submit"
            size="lg"
            loading={submitting}
            disabled={submitting || !brandJson.trim()}
          >
            {submitting ? 'Creating site…' : 'Create site'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="lg"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

